import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import GroupWorkRoundedIcon from '@mui/icons-material/GroupWorkRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import {
  getAccountPartnerMembership,
  getMembershipDisplayName,
} from '../utils/userProfile';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const emptyForm = {
  owning_partner_id: '',
  name: '',
  description: '',
  visibility: 'private',
  partner_ids: [],
};

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function moveItem(items, fromIndex, toIndex) {
  if (toIndex < 0 || toIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, item);
  return nextItems;
}

function buildValidationMessage(form) {
  if (!form.owning_partner_id) {
    return 'An associated partner or business unit is required before saving.';
  }
  if (!form.name.trim()) {
    return 'Enter a name for the preferred partner list.';
  }
  if (!form.partner_ids.length) {
    return 'Add at least one partner to the list.';
  }
  if (form.partner_ids.includes(form.owning_partner_id)) {
    return 'The owning partner cannot also be a list member.';
  }
  if (new Set(form.partner_ids).size !== form.partner_ids.length) {
    return 'Each member partner can only appear once.';
  }
  return '';
}

function visibilityConfig(visibility) {
  if (visibility === 'partner_shared') {
    return {
      label: 'Partner Shared',
      color: 'secondary',
      icon: <GroupWorkRoundedIcon />,
    };
  }

  return {
    label: 'Private',
    color: 'default',
    icon: <LockRoundedIcon />,
  };
}

export default function PreferredPartnerLists(props) {
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const accountMembership = getAccountPartnerMembership(partnerProfile);
  const memberships = accountMembership ? [accountMembership] : [];

  const [lists, setLists] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingList, setEditingList] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [partnerToAdd, setPartnerToAdd] = useState('');

  const orderedMemberships = memberships;

  const catalogById = catalog.reduce((accumulator, partner) => {
    accumulator[partner.partner_id] = partner;
    return accumulator;
  }, {});

  const membershipById = orderedMemberships.reduce((accumulator, membership) => {
    accumulator[membership.partner_id] = membership;
    return accumulator;
  }, {});

  const activeMembershipOptions = orderedMemberships.map((membership) => ({
    partner_id: membership.partner_id,
    display_name: membership.display_name || membership.partner_code || membership.partner_id,
    partner_code: membership.partner_code,
  }));

  const availableMemberOptions = catalog.filter((partner) => (
    partner.status === 'active'
      && partner.partner_id !== form.owning_partner_id
      && !form.partner_ids.includes(partner.partner_id)
  ));

  function getPartnerLabel(partnerId) {
    const membership = membershipById[partnerId];
    if (membership) {
      return membership.display_name || membership.partner_code || partnerId;
    }

    const partner = catalogById[partnerId];
    if (partner) {
      return partner.display_name || partner.partner_code || partnerId;
    }

    return partnerId;
  }

  function getPartnerSubtitle(partnerId) {
    const membership = membershipById[partnerId];
    const partner = catalogById[partnerId];
    return membership?.partner_code || partner?.partner_code || '';
  }

  async function loadData() {
    setLoading(true);
    setError('');

    try {
      const [listsResponse, catalogResponse] = await Promise.all([
        axiosInstance.get('/partners/user/preferred-partner-lists'),
        axiosInstance.get('/partners/user/catalog'),
      ]);

      setLists(Array.isArray(listsResponse.data?.lists) ? listsResponse.data.lists : []);
      setCatalog(Array.isArray(catalogResponse.data?.partners) ? catalogResponse.data.partners : []);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load preferred partner lists right now.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function closeDialog() {
    setDialogOpen(false);
    setDialogMode('create');
    setEditingList(null);
    setForm(emptyForm);
    setFormError('');
    setPartnerToAdd('');
  }

  function openCreateDialog() {
    const defaultOwner = accountMembership?.partner_id || '';
    setDialogMode('create');
    setEditingList(null);
    setForm({
      ...emptyForm,
      owning_partner_id: defaultOwner,
    });
    setFormError('');
    setPartnerToAdd('');
    setDialogOpen(true);
  }

  function openEditDialog(list) {
    setDialogMode('edit');
    setEditingList(list);
    setForm({
      owning_partner_id: list.owning_partner_id || '',
      name: list.name || '',
      description: list.description || '',
      visibility: list.visibility || 'private',
      partner_ids: Array.isArray(list.members) ? list.members.map((member) => member.partner_id) : [],
    });
    setFormError('');
    setPartnerToAdd('');
    setDialogOpen(true);
  }

  function handleAddMember() {
    if (!partnerToAdd) {
      return;
    }

    setForm((current) => ({
      ...current,
      partner_ids: [...current.partner_ids, partnerToAdd],
    }));
    setPartnerToAdd('');
  }

  function handleRemoveMember(partnerId) {
    setForm((current) => ({
      ...current,
      partner_ids: current.partner_ids.filter((currentPartnerId) => currentPartnerId !== partnerId),
    }));
  }

  function handleMoveMember(index, direction) {
    setForm((current) => ({
      ...current,
      partner_ids: moveItem(current.partner_ids, index, index + direction),
    }));
  }

  async function handleSubmit() {
    const validationMessage = buildValidationMessage(form);
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }

    setSubmitting(true);
    setFormError('');
    setFeedback('');

    const payload = {
      owning_partner_id: form.owning_partner_id,
      name: form.name.trim(),
      description: form.description.trim(),
      visibility: form.visibility,
      partner_ids: form.partner_ids,
      metadata: editingList?.metadata || {},
    };

    try {
      if (dialogMode === 'edit' && editingList?.list_id) {
        await axiosInstance.patch(`/partners/user/preferred-partner-lists/${editingList.list_id}`, payload);
        setFeedback('Preferred partner list updated.');
      } else {
        await axiosInstance.post('/partners/user/preferred-partner-lists', payload);
        setFeedback('Preferred partner list created.');
      }

      closeDialog();
      await loadData();
    } catch (requestError) {
      setFormError(requestError?.response?.data?.message || 'Unable to save the preferred partner list.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(list) {
    const confirmed = window.confirm(`Delete "${list.name}"? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setDeletingId(list.list_id);
    setError('');
    setFeedback('');

    try {
      await axiosInstance.delete(`/partners/user/preferred-partner-lists/${list.list_id}`);
      setFeedback('Preferred partner list deleted.');
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to delete the preferred partner list.');
    } finally {
      setDeletingId('');
    }
  }

  const ownedLists = lists.filter((list) => list.can_edit);
  const sharedReadOnlyLists = lists.filter((list) => !list.can_edit);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Preferred Partner Lists' },
            ]}
          />

          <Stack spacing={3}>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                overflow: 'hidden',
                position: 'relative',
                color: 'common.white',
                background: 'linear-gradient(135deg, #12355b 0%, #1b4965 45%, #5fa8d3 100%)',
                boxShadow: '0 24px 60px rgba(6, 24, 44, 0.24)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  background:
                    'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.14), transparent 24%), radial-gradient(circle at 82% 28%, rgba(255,255,255,0.12), transparent 22%)',
                }}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                <Stack spacing={1.5} sx={{ maxWidth: 760 }}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.24em', opacity: 0.9 }}>
                    PARTNER ROUTING
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Manage reusable preferred partner lists
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.88 }}>
                    Create ordered partner groups from your associated partner/business unit, keep private lists for your own work, and share curated lists across your partner organization when needed.
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Chip
                    icon={<PlaylistAddCheckRoundedIcon />}
                    label={`${lists.length} visible lists`}
                    variant="outlined"
                    sx={{ color: 'common.white', borderColor: alpha('#ffffff', 0.34) }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={openCreateDialog}
                    disabled={!activeMembershipOptions.length}
                    sx={{
                      bgcolor: 'common.white',
                      color: 'primary.main',
                      '&:hover': { bgcolor: alpha('#ffffff', 0.92) },
                    }}
                  >
                    New List
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {feedback ? <Alert severity="success">{feedback}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            {!activeMembershipOptions.length ? (
              <Alert severity="warning">
                No associated partner or business unit was found for your user account, so you cannot create preferred partner lists yet.
              </Alert>
            ) : null}

            {loading ? (
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={24} />
                  <Typography>Loading preferred partner lists...</Typography>
                </Stack>
              </Paper>
            ) : (
              <Stack spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Your editable lists
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Lists you created can be updated, reordered, or deleted here.
                      </Typography>
                    </Box>
                    <Chip label={`${ownedLists.length} owned`} color="primary" variant="outlined" />
                  </Stack>

                  <Stack spacing={2.5} sx={{ mt: 3 }}>
                    {!ownedLists.length ? (
                      <Alert severity="info">
                        You do not have any preferred partner lists yet. Create one to start building reusable partner routing groups.
                      </Alert>
                    ) : (
                      ownedLists.map((list) => {
                        const visibility = visibilityConfig(list.visibility);

                        return (
                          <Card
                            key={list.list_id}
                            sx={{
                              borderRadius: 4,
                              border: '1px solid',
                              borderColor: 'divider',
                              boxShadow: 'none',
                              background: (theme) =>
                                list.visibility === 'partner_shared'
                                  ? `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(
                                    theme.palette.background.paper,
                                    0.98,
                                  )} 56%, ${alpha(theme.palette.info.light, 0.12)} 100%)`
                                  : theme.palette.background.paper,
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                  <Box>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {list.name}
                                      </Typography>
                                      <Chip size="small" icon={visibility.icon} label={visibility.label} color={visibility.color} variant="outlined" />
                                      <Chip size="small" label={`${list.members?.length || 0} members`} variant="outlined" />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Owned by {getPartnerLabel(list.owning_partner_id)}
                                      {getPartnerSubtitle(list.owning_partner_id) ? ` • ${getPartnerSubtitle(list.owning_partner_id)}` : ''}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Updated {formatDateTime(list.updated_at)}
                                  </Typography>
                                </Stack>

                                <Typography color="text.secondary">
                                  {list.description || 'No description provided.'}
                                </Typography>

                                <Divider />

                                <Stack spacing={1.25}>
                                  {Array.isArray(list.members) && list.members.length ? (
                                    list.members.map((member, index) => (
                                      <Stack
                                        key={`${list.list_id}-${member.partner_id}`}
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                        sx={{
                                          p: 1.5,
                                          borderRadius: 2.5,
                                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                                        }}
                                      >
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                          <Chip size="small" label={`#${index + 1}`} color="primary" />
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {member.display_name || member.partner_code || member.partner_id}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {[member.partner_code, member.status].filter(Boolean).join(' • ')}
                                            </Typography>
                                          </Box>
                                        </Stack>
                                      </Stack>
                                    ))
                                  ) : (
                                    <Alert severity="warning">This list has no members.</Alert>
                                  )}
                                </Stack>
                              </Stack>
                            </CardContent>
                            <CardActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end' }}>
                              <Button
                                variant="outlined"
                                startIcon={<EditRoundedIcon />}
                                onClick={() => openEditDialog(list)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="error"
                                variant="outlined"
                                startIcon={deletingId === list.list_id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRoundedIcon />}
                                onClick={() => handleDelete(list)}
                                disabled={deletingId === list.list_id}
                              >
                                Delete
                              </Button>
                            </CardActions>
                          </Card>
                        );
                      })
                    )}
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Shared lists you can view
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Partner-shared lists for your associated partner/business unit remain visible here even when you are not the list creator.
                      </Typography>
                    </Box>
                    <Chip label={`${sharedReadOnlyLists.length} read-only`} icon={<VisibilityRoundedIcon />} variant="outlined" />
                  </Stack>

                  <Stack spacing={2.5} sx={{ mt: 3 }}>
                    {!sharedReadOnlyLists.length ? (
                      <Alert severity="info">
                        No read-only shared lists are currently visible to your account.
                      </Alert>
                    ) : (
                      sharedReadOnlyLists.map((list) => (
                        <Card
                          key={list.list_id}
                          sx={{
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: alpha('#7b1fa2', 0.2),
                            boxShadow: 'none',
                            background: (theme) =>
                              `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(
                                theme.palette.background.paper,
                                0.98,
                              )} 70%, ${alpha(theme.palette.secondary.light, 0.12)} 100%)`,
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                <Box>
                                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                      {list.name}
                                    </Typography>
                                    <Chip size="small" icon={<GroupWorkRoundedIcon />} label="Partner Shared" color="secondary" />
                                    <Chip size="small" icon={<VisibilityRoundedIcon />} label="Read only" variant="outlined" />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Owned by {getPartnerLabel(list.owning_partner_id)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  Updated {formatDateTime(list.updated_at)}
                                </Typography>
                              </Stack>
                              <Typography color="text.secondary">
                                {list.description || 'No description provided.'}
                              </Typography>
                              <Divider />
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {(list.members || []).map((member, index) => (
                                  <Chip
                                    key={`${list.list_id}-${member.partner_id}`}
                                    label={`#${index + 1} ${member.display_name || member.partner_code || member.partner_id}`}
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>

          <Dialog open={dialogOpen} onClose={submitting ? undefined : closeDialog} fullWidth maxWidth="md">
            <DialogTitle>
              {dialogMode === 'edit' ? 'Edit Preferred Partner List' : 'Create Preferred Partner List'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5} sx={{ pt: 1 }}>
                {formError ? <Alert severity="error">{formError}</Alert> : null}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      flex: 1,
                      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.32),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                      Owning Partner/Business Unit
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, overflowWrap: 'anywhere' }}>
                      {getPartnerLabel(form.owning_partner_id || accountMembership?.partner_id) || getMembershipDisplayName(accountMembership, 'Unavailable')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getPartnerSubtitle(form.owning_partner_id || accountMembership?.partner_id) || form.owning_partner_id || accountMembership?.partner_id || 'Unavailable'}
                    </Typography>
                  </Paper>

                  <TextField
                    select
                    label="Visibility"
                    value={form.visibility}
                    onChange={(event) => setForm((current) => ({ ...current, visibility: event.target.value }))}
                    fullWidth
                  >
                    <MenuItem value="private">Private</MenuItem>
                    <MenuItem value="partner_shared">Partner Shared</MenuItem>
                  </TextField>
                </Stack>

                <TextField
                  label="List Name"
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  fullWidth
                  required
                />

                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                  helperText="Optional context for when to use this preferred list."
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Partner members
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Add partners from the active catalog, then use the arrows to keep the routing order exactly how you want it.
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Add Partner"
                        value={partnerToAdd}
                        onChange={(event) => setPartnerToAdd(event.target.value)}
                        fullWidth
                        helperText={!availableMemberOptions.length ? 'All eligible partners are already in the list.' : 'Only active partners outside the owning partner are available.'}
                      >
                        {availableMemberOptions.map((partner) => (
                          <MenuItem key={partner.partner_id} value={partner.partner_id}>
                            {partner.display_name || partner.partner_code || partner.partner_id}
                            {partner.partner_code ? ` (${partner.partner_code})` : ''}
                            {partner.is_user_member ? ' • Your associated partner' : ''}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleAddMember}
                        disabled={!partnerToAdd}
                        sx={{ minWidth: { md: 140 } }}
                      >
                        Add Member
                      </Button>
                    </Stack>

                    <Stack spacing={1.5}>
                      {!form.partner_ids.length ? (
                        <Alert severity="info">No partners have been added yet.</Alert>
                      ) : (
                        form.partner_ids.map((partnerId, index) => (
                          <Paper
                            key={`${partnerId}-${index}`}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                            }}
                          >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Chip size="small" label={`#${index + 1}`} color="primary" />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {getPartnerLabel(partnerId)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {getPartnerSubtitle(partnerId) || partnerId}
                                  </Typography>
                                </Box>
                              </Stack>

                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveMember(index, -1)}
                                  disabled={index === 0}
                                  aria-label={`Move ${getPartnerLabel(partnerId)} up`}
                                >
                                  <ArrowUpwardRoundedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveMember(index, 1)}
                                  disabled={index === form.partner_ids.length - 1}
                                  aria-label={`Move ${getPartnerLabel(partnerId)} down`}
                                >
                                  <ArrowDownwardRoundedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveMember(partnerId)}
                                  aria-label={`Remove ${getPartnerLabel(partnerId)}`}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Paper>
                        ))
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {dialogMode === 'edit' ? 'Save Changes' : 'Create List'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </AppTheme>
  );
}
